import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  MessageSquare,
  Star,
  Eye,
  Trash2,
  Filter,
  Mail,
  Phone,
  Calendar,
  ThumbsUp,
  ThumbsDown,
  Loader2,
  CheckCircle,
  Archive,
  BarChart3
} from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';

export default function FeedbackManagementPage() {
  const { api, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [feedbackList, setFeedbackList] = useState([]);
  const [stats, setStats] = useState({});
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [response, setResponse] = useState('');
  const [saving, setSaving] = useState(false);

  const isSuperAdmin = user?.email?.toLowerCase() === 'admin@icrcl.co.uk';

  useEffect(() => {
    fetchFeedback();
  }, [filterStatus, filterType]);

  const fetchFeedback = async () => {
    setLoading(true);
    try {
      let url = '/feedback/list?';
      if (filterStatus !== 'all') url += `status=${filterStatus}&`;
      if (filterType !== 'all') url += `feedback_type=${filterType}`;
      
      const [feedbackRes, statsRes] = await Promise.all([
        api.get(url),
        api.get('/feedback/stats/summary')
      ]);
      
      setFeedbackList(feedbackRes.data.feedback || []);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Failed to fetch feedback:', error);
      toast.error('Failed to load feedback');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (feedbackId, status) => {
    try {
      await api.put(`/feedback/${feedbackId}/status`, { 
        status,
        response: response || undefined
      });
      toast.success(`Feedback marked as ${status}`);
      setShowDetailDialog(false);
      setResponse('');
      fetchFeedback();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const deleteFeedback = async (feedbackId) => {
    if (!window.confirm('Are you sure you want to delete this feedback?')) return;
    
    try {
      await api.delete(`/feedback/${feedbackId}`);
      toast.success('Feedback deleted');
      fetchFeedback();
    } catch (error) {
      toast.error('Failed to delete feedback');
    }
  };

  const openDetail = (feedback) => {
    setSelectedFeedback(feedback);
    setResponse(feedback.response || '');
    setShowDetailDialog(true);
  };

  const getRatingStars = (rating) => {
    return (
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map(star => (
          <Star
            key={star}
            className={`w-4 h-4 ${star <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-slate-300'}`}
          />
        ))}
      </div>
    );
  };

  const getStatusBadge = (status) => {
    const config = {
      new: { color: 'bg-blue-100 text-blue-800', label: 'New' },
      reviewed: { color: 'bg-amber-100 text-amber-800', label: 'Reviewed' },
      resolved: { color: 'bg-green-100 text-green-800', label: 'Resolved' },
      archived: { color: 'bg-slate-100 text-slate-800', label: 'Archived' }
    };
    const c = config[status] || config.new;
    return <Badge className={c.color}>{c.label}</Badge>;
  };

  const getTypeBadge = (type) => {
    const colors = {
      general: 'bg-slate-100 text-slate-700',
      service: 'bg-purple-100 text-purple-700',
      therapist: 'bg-blue-100 text-blue-700',
      facilities: 'bg-amber-100 text-amber-700',
      complaint: 'bg-red-100 text-red-700',
      suggestion: 'bg-green-100 text-green-700',
      compliment: 'bg-pink-100 text-pink-700'
    };
    return (
      <Badge className={colors[type] || colors.general}>
        {type.charAt(0).toUpperCase() + type.slice(1)}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-[#1e3a5f]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="feedback-management">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#1e3a5f]">Feedback Management</h1>
          <p className="text-slate-600">View and respond to feedback from QR code submissions</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-600">Total Feedback</p>
                <p className="text-2xl font-bold text-blue-800">{stats.total || 0}</p>
              </div>
              <MessageSquare className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-yellow-600">Average Rating</p>
                <p className="text-2xl font-bold text-yellow-800">{stats.average_rating || 0}/5</p>
              </div>
              <Star className="w-8 h-8 text-yellow-500 fill-yellow-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-600">Would Recommend</p>
                <p className="text-2xl font-bold text-green-800">{stats.recommendation_rate || 0}%</p>
              </div>
              <ThumbsUp className="w-8 h-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-red-600">New/Unread</p>
                <p className="text-2xl font-bold text-red-800">{stats.by_status?.new || 0}</p>
              </div>
              <BarChart3 className="w-8 h-8 text-red-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex gap-4 items-center">
        <Filter className="w-5 h-5 text-slate-500" />
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="new">New</SelectItem>
            <SelectItem value="reviewed">Reviewed</SelectItem>
            <SelectItem value="resolved">Resolved</SelectItem>
            <SelectItem value="archived">Archived</SelectItem>
          </SelectContent>
        </Select>

        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="general">General</SelectItem>
            <SelectItem value="service">Service</SelectItem>
            <SelectItem value="therapist">Therapist</SelectItem>
            <SelectItem value="facilities">Facilities</SelectItem>
            <SelectItem value="complaint">Complaint</SelectItem>
            <SelectItem value="suggestion">Suggestion</SelectItem>
            <SelectItem value="compliment">Compliment</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Feedback List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-[#2a9d8f]" />
            Feedback Submissions
          </CardTitle>
        </CardHeader>
        <CardContent>
          {feedbackList.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <MessageSquare className="w-12 h-12 mx-auto mb-2 text-slate-300" />
              <p>No feedback found</p>
            </div>
          ) : (
            <div className="space-y-3">
              {feedbackList.map(feedback => (
                <div 
                  key={feedback.id} 
                  className={`p-4 border rounded-lg hover:bg-slate-50 cursor-pointer ${feedback.status === 'new' ? 'border-l-4 border-l-blue-500' : ''}`}
                  onClick={() => openDetail(feedback)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-[#1e3a5f]">
                          {feedback.name || 'Anonymous'}
                        </span>
                        {getRatingStars(feedback.rating)}
                        {getStatusBadge(feedback.status)}
                        {getTypeBadge(feedback.feedback_type)}
                      </div>
                      <p className="text-sm text-slate-600 line-clamp-2 mb-2">
                        {feedback.message}
                      </p>
                      <div className="flex items-center gap-4 text-xs text-slate-500">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {format(parseISO(feedback.submitted_at), 'MMM d, yyyy HH:mm')}
                        </span>
                        {feedback.service_received && (
                          <span>Service: {feedback.service_received}</span>
                        )}
                        {feedback.email && (
                          <span className="flex items-center gap-1">
                            <Mail className="w-3 h-3" />
                            {feedback.email}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); openDetail(feedback); }}>
                        <Eye className="w-4 h-4 text-blue-500" />
                      </Button>
                      {isSuperAdmin && (
                        <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); deleteFeedback(feedback.id); }}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#1e3a5f]">
              <MessageSquare className="w-5 h-5" />
              Feedback Details
            </DialogTitle>
          </DialogHeader>

          {selectedFeedback && (
            <div className="space-y-4">
              {/* Rating and Status */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {getRatingStars(selectedFeedback.rating)}
                  <span className="font-medium">({selectedFeedback.rating}/5)</span>
                </div>
                <div className="flex gap-2">
                  {getStatusBadge(selectedFeedback.status)}
                  {getTypeBadge(selectedFeedback.feedback_type)}
                </div>
              </div>

              {/* Contact Info */}
              <div className="p-4 bg-slate-50 rounded-lg">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-slate-500">Name</p>
                    <p className="font-medium">{selectedFeedback.name || 'Anonymous'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Service</p>
                    <p className="font-medium">{selectedFeedback.service_received || 'Not specified'}</p>
                  </div>
                  {selectedFeedback.email && (
                    <div>
                      <p className="text-sm text-slate-500">Email</p>
                      <p className="font-medium">{selectedFeedback.email}</p>
                    </div>
                  )}
                  {selectedFeedback.phone && (
                    <div>
                      <p className="text-sm text-slate-500">Phone</p>
                      <p className="font-medium">{selectedFeedback.phone}</p>
                    </div>
                  )}
                  {selectedFeedback.therapist_name && (
                    <div>
                      <p className="text-sm text-slate-500">Therapist</p>
                      <p className="font-medium">{selectedFeedback.therapist_name}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-sm text-slate-500">Would Recommend</p>
                    <p className="font-medium">{selectedFeedback.would_recommend || 'Not specified'}</p>
                  </div>
                </div>
              </div>

              {/* Message */}
              <div>
                <p className="text-sm text-slate-500 mb-1">Feedback Message</p>
                <div className="p-4 bg-white border rounded-lg">
                  <p className="text-slate-800 whitespace-pre-wrap">{selectedFeedback.message}</p>
                </div>
              </div>

              {/* Response */}
              <div>
                <p className="text-sm text-slate-500 mb-1">Your Response</p>
                <Textarea
                  placeholder="Add a response or note about this feedback..."
                  value={response}
                  onChange={(e) => setResponse(e.target.value)}
                  rows={3}
                />
              </div>

              {/* Timestamp */}
              <p className="text-xs text-slate-500">
                Submitted: {format(parseISO(selectedFeedback.submitted_at), 'MMMM d, yyyy at HH:mm')}
                {selectedFeedback.reviewed_at && (
                  <span> • Reviewed: {format(parseISO(selectedFeedback.reviewed_at), 'MMM d, yyyy HH:mm')}</span>
                )}
              </p>
            </div>
          )}

          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setShowDetailDialog(false)}>Close</Button>
            <Button 
              variant="outline"
              onClick={() => updateStatus(selectedFeedback.id, 'archived')}
            >
              <Archive className="w-4 h-4 mr-1" />
              Archive
            </Button>
            <Button 
              className="bg-amber-600 hover:bg-amber-700"
              onClick={() => updateStatus(selectedFeedback.id, 'reviewed')}
            >
              Mark Reviewed
            </Button>
            <Button 
              className="bg-green-600 hover:bg-green-700"
              onClick={() => updateStatus(selectedFeedback.id, 'resolved')}
            >
              <CheckCircle className="w-4 h-4 mr-1" />
              Resolved
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
